<script>
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const MAXIMUM_FILE_UPLOAD_SIZE = 30; // in MB

export default {
  components: {
    Spinner,
  },
  mixins: [alertMixin],
  props: {
    initialFileName: {
      type: String,
      default: '',
    },
    accept: {
      type: String,
      default: 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
    },
    label: {
      type: String,
      default: 'Upload Media File',
    },
  },
  data() {
    return {
      uploadState: 'idle',
      uploadLabel: this.label,
      fileUrl: '',
    };
  },
  mounted() {
    if (this.initialFileName) {
      this.uploadLabel = this.initialFileName;
      this.uploadState = 'uploaded';
    }
  },
  methods: {
    onChangeFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.uploadMediaToStorage(file);
      } else {
        this.showAlert(
          this.$t(
            'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.IMAGE_UPLOAD_SIZE_ERROR',
            {
              size: MAXIMUM_FILE_UPLOAD_SIZE,
            }
          )
        );
        this.uploadState = 'failed';
        this.uploadLabel = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
      }
    },
    async uploadMediaToStorage(file) {
      this.uploadState = 'processing';
      this.uploadLabel = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADING');
      try {
        const { fileUrl } = await uploadFile(file);
        if (fileUrl) {
          this.fileUrl = fileUrl;
          this.$emit('update:modelValue', fileUrl);
          this.uploadState = 'uploaded';
          this.uploadLabel =
            file.name || this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADED');
          return;
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
      this.uploadState = 'failed';
      this.uploadLabel = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
      this.showAlert(this.$t('AUTOMATION.ATTACHMENT.UPLOAD_ERROR'));
    },
    removeFile() {
      this.fileUrl = '';
      this.uploadState = 'idle';
      this.uploadLabel = this.label;
      this.$emit('update:modelValue', '');
    },
  },
};
</script>

<template>
  <div class="campaign-media-upload">
    <label class="input-wrapper flex-1 text-sm" :class="uploadState">
      <input
        v-if="uploadState !== 'processing'"
        type="file"
        name="campaign-attachment"
        :class="uploadState === 'processing' ? 'disabled' : ''"
        :accept="accept"
        @change="onChangeFile"
      />
      <Spinner v-if="uploadState === 'processing'" />
      <fluent-icon v-if="uploadState === 'idle'" icon="file-upload" />
      <fluent-icon
        v-if="uploadState === 'uploaded'"
        icon="checkmark-circle"
        type="outline"
        class="success-icon"
      />
      <fluent-icon
        v-if="uploadState === 'failed'"
        icon="dismiss-circle"
        type="outline"
        class="error-icon"
      />
      <p class="file-button">{{ uploadLabel }}</p>
    </label>

    <div v-if="uploadState === 'uploaded'" class="file-actions">
      <woot-button variant="clear" size="small" @click="removeFile">
        {{ $t('CAMPAIGN.MEDIA.REMOVE_FILE') }}
      </woot-button>
    </div>
  </div>
</template>

<style scoped>
.campaign-media-upload {
  @apply w-full;
}

input[type='file'] {
  @apply hidden;
}

.input-wrapper {
  @apply flex h-9 bg-white dark:bg-slate-900 py-1 px-2 items-center text-xs cursor-pointer rounded-sm border border-dashed border-woot-100 dark:border-woot-500;
}

.success-icon {
  @apply text-green-500 dark:text-green-600 mr-2;
}

.error-icon {
  @apply text-red-500 dark:text-red-600 mr-2;
}

.processing {
  @apply cursor-not-allowed opacity-90;
}

.file-button {
  @apply whitespace-nowrap overflow-hidden text-ellipsis w-full mb-0;
}

.file-actions {
  @apply mt-2 flex justify-end;
}
</style>
